import React, { useContext } from 'react';
import { palettes, neutralPalettes } from '#lib/helpers/tailwind.js';
import SnapScroll from '#components/snap-scroll.jsx';
import select from '#lib/components/select.jsx';
import { addClasses, cn, mergeClasses } from '#lib/helpers/class.js';
import Input from '#lib/components/input.jsx';
import { Icon } from '@iconify-icon/react';
import { ColorRadio, CustomPickers, CustomRadio, NoneColorRadio } from './pickers.jsx';
import {
    defaultMakeReactKey,
    defaultAccessValue,
    defaultDisplayValue,
    setOptions,
    translateLanguages
} from '#helpers/helpers.js';
import { displayValuePalette } from './helpers.jsx';
import FloatingTooltip from '#components/tooltip.jsx';
import FilePreview from '#components/file-preview.jsx';
import ImagePreview from '#components/images/image-preview.jsx';
import Checkbox from '#lib/components/checkbox.jsx';
import File from '#lib/components/file-dnd.jsx';
import { AudioPreview } from '#components/audio-preview.jsx';
import HTMLEditor from '#components/html-editor.jsx';
import { usePack, usePurchases } from '#src/@billing/use-billing.js';
import { findValidItem } from '#routes/settings/billing/billing-helpers.js';
import { UserContext } from '#src/index.jsx';
import { useCurrencies } from '#hooks/use-currencies.js';

// THEME COMPONENT
export function Detail(props) {
    const {
        themes,
        selectedProfile,
        editProfile,
        handleChange,
        handleChangeDropdown,
        errors,
        supportedLanguages,
        selectedTheme,
        mediaLogo,
        logo,
        defaultCover,
        mediaCover,
        defaultIntro,
        mediaIntro,
        defaultJingle,
        mediaJingle,
        customerContent,
        companyContent,
        primaryColors,
        neutralColors
    } = props;

    // gli errori vengono tracciati con l'oggetto errors che è un collezione di key-value pair con:
    // key: campo del profile
    // value: se true significa che il rispettivo campo non è valorizzato su profile
    const { data: purchases } = usePurchases();
    const { data: packages } = usePack();
    const activePackage = findValidItem(purchases, packages);
    const isPremium = activePackage?.priority >= 2;
    const { getLanguage } = useContext(UserContext);
    const lang = getLanguage();

    const { currencies } = useCurrencies();

    if (!selectedProfile)
        return (
            <section className="hidden w-full lg:w-2/3 lg:flex p-16 flex-col items-center space-y-6 lg:bg-neutral-50">
                <p className="text-2xl font-bold text-neutral-500">{_t.theme.emptyDetailText()}</p>
                <p className="text-lg text-center font-bold text-neutral-400">{_t.theme.emptyDetailSubText()}</p>
            </section>
        );

    return (
        <div className="w-auto lg:h-full lg:w-1/2 lg:px-4 flex flex-col space-y-3 lg: lg:border-l lg:border-l-neutral-200 overflow-auto p-2">
            <div className="w-full flex gap-1 items-center justify-between">
                <input
                    name="name"
                    className={cn(
                        'w-1/2 text-xl font-bold text-ellipsis border-b border-transparent bg-transparent focus:outline-none',
                        errors?.name
                            ? 'bg-red-100 placeholder-red-500 hover:border-red-500'
                            : 'text-neutral-700 hover:border-gray-300 focus:border-gray-500 '
                    )}
                    value={selectedProfile?.name}
                    placeholder={_t.profiles.profileName()}
                    onChange={handleChange}
                />
            </div>
            <div className="flex gap-2 h-[20px]">
                <p className="font-semibold text-neutral-700">{_t.theme.selectFavorite()}: </p>
                {selectedProfile?.isDefault ? (
                    <Icon
                        icon="material-symbols:favorite-rounded"
                        width="1.2rem"
                        height="1.2rem"
                        className="pt-[2px] text-[#c51111]"
                    />
                ) : (
                    <FloatingTooltip content={_t.theme.selectFavorite()} placement="bottom">
                        <Icon
                            icon="material-symbols:favorite-outline-rounded"
                            width="1.2rem"
                            height="1.2rem"
                            className="pt-[2px] text-[#c51111] cursor-pointer"
                            onClick={() => editProfile(selectedProfile.id, 'isDefault', true)}
                        />
                    </FloatingTooltip>
                )}
            </div>

            <p className="text-lg font-bold text-neutral-700">{_t.theme.companyInfo()}</p>

            <p className="mb-2">{_t.theme.companyLogo()}</p>
            <div className="w-[160px] min-h-[160px] h-[160px]">
                <FilePreview
                    name="logo"
                    file={logo}
                    coords={logo && logo?.coords}
                    width={270}
                    height={270}
                    isSquare={true}
                    accept="image/png, image/jpeg"
                    isGallery={false}
                    {...mediaLogo.handlers}
                >
                    {({ previewFile }) => {
                        const initialState = {
                            ...previewFile?.coords,
                            containerWidth: 270
                        };
                        const src = previewFile?.tempSrc ?? previewFile?.src;
                        return <>{previewFile && <ImagePreview imageSrc={src} initialState={initialState} />}</>;
                    }}
                </FilePreview>
            </div>
            <div className="flex-col space-y-2 w-full">
                <p>{_t.theme.companyName()}</p>
                <div className="flex items-center gap-4">
                    <Input.Full
                        type="text"
                        value={selectedProfile?.companyName || ''}
                        name="companyName"
                        placeholder={_t.theme.companyName()}
                        onChange={handleChange}
                        hasError={errors?.companyName}
                    />
                    <Checkbox.Full
                        name="showName"
                        label={_t.edit.fields.showName()}
                        checked={selectedProfile?.showName}
                        onChange={handleChange}
                    />
                    <Checkbox.Full
                        name="animateOnlyOneTime"
                        label={_t.edit.fields.animateOnlyOneTime()}
                        checked={selectedProfile?.animateOnlyOneTime}
                        onChange={handleChange}
                    />
                </div>
            </div>
            <p className="text-lg font-bold text-neutral-700">{_t.theme.defaults()}</p>
            <div className="flex flex-wrap gap-4">
                <div className="flex flex-col space-y-2">
                    <p>{_t.theme.defaultCover()}</p>
                    <div className="w-[300px]">
                        <FilePreview
                            name="defaultCover"
                            file={defaultCover}
                            coords={defaultCover && defaultCover?.coords}
                            width={480}
                            height={270}
                            accept="image/png, image/jpeg"
                            isGallery={false}
                            {...mediaCover.handlers}
                        >
                            {({ previewFile }) => {
                                const src = previewFile?.tempSrc ?? previewFile?.src;
                                return (
                                    <>
                                        {previewFile && (
                                            <ImagePreview imageSrc={src} initialState={previewFile?.coords} />
                                        )}
                                    </>
                                );
                            }}
                        </FilePreview>
                    </div>
                </div>
                <div className="flex flex-col space-y-2">
                    <p>{_t.theme.defaultIntro()}</p>
                    <div className="w-[300px]">
                        <FilePreview
                            name="defaultIntro"
                            file={defaultIntro}
                            coords={defaultIntro && defaultIntro?.coords}
                            width={480}
                            height={270}
                            accept="image/png, image/jpeg"
                            isGallery={false}
                            {...mediaIntro.handlers}
                        >
                            {({ previewFile }) => {
                                const initialState = {
                                    ...previewFile?.coords,
                                    containerWidth: 480,
                                    containerHeight: 270
                                };
                                const src = previewFile?.tempSrc ?? previewFile?.src;
                                return (
                                    <>{previewFile && <ImagePreview imageSrc={src} initialState={initialState} />}</>
                                );
                            }}
                        </FilePreview>
                    </div>
                </div>
                <div className="flex flex-col space-y-2">
                    <p>{_t.theme.defaultJingle()}</p>
                    <div className="w-[300px] h-[169px]">
                        <File
                            name="defaultJingle"
                            existingFiles={[defaultJingle]}
                            type="audio"
                            className={addClasses('h-full')}
                            accept="audio/*"
                            {...mediaJingle.handlers}
                        >
                            {({ hasFiles, file, helperProps, clear }) => (
                                <>{hasFiles && !helperProps.isDragging && <AudioPreview file={file} clear={clear} />}</>
                            )}
                        </File>
                    </div>
                </div>
            </div>

            <p className="text-lg font-bold text-neutral-700">{_t.profiles.defaultTexts()}</p>
            <div className="w-full max-w-[620px] flex flex-col gap-3">
                <div>
                    <p>{_t.profiles.defaultCustomerContent()}</p>
                    <HTMLEditor
                        onEditorChange={(_, editor) =>
                            handleChange({ target: { name: 'customerContent', value: editor.getContent() } })
                        }
                        value={customerContent}
                    />
                </div>

                {isPremium && (
                    <div>
                        <p>{_t.profiles.defaultCompanyContent()}</p>
                        <HTMLEditor
                            onEditorChange={(_, editor) =>
                                handleChange({ target: { name: 'companyContent', value: editor.getContent() } })
                            }
                            value={companyContent}
                        />
                    </div>
                )}
            </div>

            <p className="text-lg font-bold text-neutral-700">{_t.edit.currencyAndPrices()}</p>
            <div className="flex flex-wrap items-center gap-4">
                <div className="w-full sm:w-1/3">
                    <select.Full
                        name="currency"
                        options={currencies}
                        onChange={(value) => handleChangeDropdown('currency', value)}
                        makeReactKey={defaultMakeReactKey}
                        accessValue={defaultAccessValue}
                        displayValue={defaultDisplayValue}
                        value={currencies?.find(
                            (el) => el.id === selectedProfile?.currency || el.name === selectedProfile?.currency
                        )}
                        hasError={errors?.currency}
                    />
                </div>
                <Checkbox.Full
                    name="enableTotalPrice"
                    label={_t.edit.enableTotalPrice()}
                    checked={selectedProfile?.enableTotalPrice}
                    onChange={handleChange}
                />
                <Checkbox.Full
                    name="enablePlanPrice"
                    label={_t.edit.enablePlanPrice()}
                    checked={selectedProfile?.enablePlanPrice}
                    onChange={handleChange}
                />
            </div>

            <p className="text-lg font-bold text-neutral-700">{_t.theme.styles()}</p>
            <div className="flex-col space-y-2 w-full md:w-1/2">
                <p>{_t.sideboard.theme()}</p>
                <select.Full
                    name="themeId"
                    className={addClasses('cursor-pointer')}
                    options={themes}
                    onChange={(value) => handleChangeDropdown('themeId', value)}
                    makeReactKey={defaultMakeReactKey}
                    accessValue={defaultAccessValue}
                    displayValue={defaultDisplayValue}
                    value={selectedTheme?.name}
                    hasError={errors?.themeId}
                />
            </div>
            <div className="flex-col space-y-2 w-full md:w-1/2">
                <p className="mb-3">{_t.theme.language()}</p>
                <FloatingTooltip
                    content={selectedTheme ? _t.theme.noSupportedLanguages() : _t.theme.noSelectedTheme()}
                    className={addClasses(cn(supportedLanguages?.length > 0 && 'hidden'))}
                >
                    <select.Full
                        name="language"
                        className={addClasses('cursor-pointer')}
                        options={translateLanguages(supportedLanguages, lang)}
                        onChange={(value) => handleChangeDropdown('language', value)}
                        makeReactKey={defaultMakeReactKey}
                        accessValue={(value) => {
                            return value;
                        }}
                        displayValue={(value) => {
                            return value?.name;
                        }}
                        value={supportedLanguages.find((el) => el.id === selectedProfile?.language)}
                        disabled={supportedLanguages?.length === 0}
                        hasError={errors?.language}
                    />
                </FloatingTooltip>
            </div>
            <div className="flex justify-between gap-2 flex-col">
                <div className="w-full md:w-1/2 lg:w-full space-y-2">
                    <div className="flex max-h-6 gap-x-1">
                        <p>{_t.theme.primary()}</p>
                        {errors?.['primaryPalette'] ? (
                            <FloatingTooltip content={_t.theme.invalidColorField()} placement="right">
                                <Icon
                                    icon="ic:round-error-outline"
                                    width="1.2rem"
                                    height="1.2rem"
                                    className="mt-[1px] text-red-500 cursor-pointer"
                                />
                            </FloatingTooltip>
                        ) : (
                            ''
                        )}
                    </div>
                    <SnapScroll className={addClasses('p-2 hidden lg:flex')}>
                        {Object.entries(palettes).map(([colorName, shades]) => {
                            return (
                                <SnapScroll.Item key={colorName}>
                                    <ColorRadio
                                        shades={shades}
                                        label={colorName}
                                        name={'primaryPalette'}
                                        value={colorName}
                                        selected={selectedProfile?.primaryPalette === colorName}
                                        onChange={handleChange}
                                    />
                                </SnapScroll.Item>
                            );
                        })}
                        <SnapScroll.Item>
                            <CustomRadio
                                label={_t.edit.fields.custom()}
                                selected={selectedProfile?.primaryPalette === 'custom'}
                                name={'primaryPalette'}
                                value={'custom'}
                                onChange={handleChange}
                            />
                        </SnapScroll.Item>
                        <SnapScroll.Item>
                            <NoneColorRadio
                                label={_t.edit.fields.reset()}
                                name={'primaryPalette'}
                                value={''}
                                onChange={handleChange}
                            />
                        </SnapScroll.Item>
                    </SnapScroll>

                    <select.Full
                        name="primaryPalette"
                        className={addClasses('lg:hidden')}
                        options={setOptions(palettes)}
                        displayValue={displayValuePalette}
                        onChange={(value) => handleChangeDropdown('primaryPalette', value)}
                        value={selectedProfile?.primaryPalette}
                    />

                    {selectedProfile?.primaryPalette === 'custom' && (
                        <CustomPickers
                            colors={primaryColors}
                            type="primary"
                            editProfile={editProfile}
                            profileId={selectedProfile.id}
                        />
                    )}
                </div>

                <div className="w-full md:w-1/2 lg:w-full space-y-2">
                    <div className="flex max-h-6 gap-x-1">
                        <p>{_t.theme.neutral()}</p>
                        {errors?.['neutralPalette'] ? (
                            <FloatingTooltip content={_t.theme.invalidColorField()} placement="right">
                                <Icon
                                    icon="ic:round-error-outline"
                                    width="1.2rem"
                                    height="1.2rem"
                                    className="mt-[1px] text-red-500 cursor-pointer"
                                />
                            </FloatingTooltip>
                        ) : (
                            ''
                        )}
                    </div>
                    <SnapScroll className={addClasses('p-2 hidden lg:flex')}>
                        {Object.entries(neutralPalettes).map(([colorName, shades]) => (
                            <SnapScroll.Item key={colorName}>
                                <ColorRadio
                                    shades={shades}
                                    label={colorName}
                                    selected={selectedProfile?.neutralPalette === colorName}
                                    name={'neutralPalette'}
                                    value={colorName}
                                    onChange={handleChange}
                                />
                            </SnapScroll.Item>
                        ))}
                        <SnapScroll.Item>
                            <CustomRadio
                                label={_t.edit.fields.custom()}
                                selected={selectedProfile?.neutralPalette === 'custom'}
                                name={'neutralPalette'}
                                value={'custom'}
                                onChange={handleChange}
                            />
                        </SnapScroll.Item>
                        <SnapScroll.Item>
                            <NoneColorRadio
                                label={_t.edit.fields.reset()}
                                name={'neutralPalette'}
                                value={''}
                                onChange={handleChange}
                            />
                        </SnapScroll.Item>
                    </SnapScroll>
                    <select.Full
                        name="neutralPalette"
                        className={addClasses('flex lg:hidden')}
                        options={setOptions(neutralPalettes)}
                        displayValue={displayValuePalette}
                        onChange={(value) => handleChangeDropdown('neutralPalette', value)}
                        value={selectedProfile?.neutralPalette}
                    />

                    {selectedProfile?.neutralPalette === 'custom' && (
                        <CustomPickers
                            colors={neutralColors}
                            type="neutral"
                            editProfile={editProfile}
                            profileId={selectedProfile.id}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export function InputText(props) {
    const { value, placeholder, className } = props;
    return (
        <input
            type="text"
            className={mergeClasses(
                cn(
                    value
                        ? 'py-1 w-full text-ellipsis border-b border-transparent hover:border-gray-300 focus:text-neutral-800 focus:font-normal focus:border-gray-500 focus:outline-none'
                        : 'py-1 w-full text-ellipsis bg-red-50 border-b border-red-300 hover:border-red-500 focus:text-neutral-800 focus:font-normal focus:border-red-500 focus:outline-none'
                ),
                className
            )}
            placeholder={placeholder}
            value={value}
            {...props}
        />
    );
}
